import { useCallback, useEffect, useMemo, useState } from "react";
import { Eye, EyeOff, Loader2, Plus, Search, X } from "lucide-react";
import { toast } from "sonner";
import AdminHeader from "@/components/latir/AdminHeader";
import { useSEO } from "@/hooks/useSEO";
import { AdminLogin, apiFetch, TOKEN_KEY } from "@/pages/AdminVisitas";
import { refreshCatalog } from "@/data/catalogLive";
import { slugifyTitle } from "@/data/artists";

/* ——— Estilos (misma línea visual que AdminVisitas) ——— */
const BTN = "inline-flex items-center justify-center gap-2 min-h-[44px] px-4 py-2 border font-ui uppercase tracking-[0.14em] text-[0.62rem] transition-colors disabled:opacity-50 disabled:cursor-not-allowed";
const BTN_NEUTRAL = `${BTN} border-line text-ink-soft hover:border-ink hover:text-ink`;
const BTN_SOLID = `${BTN} border-ink bg-ink text-paper-raised hover:bg-latir-red hover:border-latir-red`;
const LABEL = "block font-ui uppercase tracking-[0.18em] text-[0.62rem] text-ink-mute mb-1.5";
const FIELD = "w-full bg-transparent border border-line focus:border-ink outline-none font-ui text-[0.85rem] px-3 py-2.5 text-ink";

const KINDS = [
  ["artists", "Obras y artistas"],
  ["salas", "Salas"],
  ["agenda", "Agenda"],
];
const KEY_FIELD = { artists: "slug", salas: "id", agenda: "id" };

/* Campos estructurados por tipo. El resto del objeto se conserva intacto
   (merge sobre el original) y se puede editar en el JSON avanzado. */
const FIELDS = {
  artists: [
    ["name", "Nombre del artista"], ["title", "Título de la obra"], ["discipline", "Disciplina"],
    ["tecnica", "Técnica"], ["medidas", "Medidas"], ["year", "Año"], ["price", "Precio"],
    ["instagram", "Instagram"], ["nacionalidad", "Nacionalidad"], ["status", "Estado interno"],
    ["image", "Imagen (ruta, ej. /brand/obra.webp)"],
    ["practica", "Práctica (ES)", "area"], ["inspiracion", "Inspiración (ES)", "area"], ["bio", "Bio (ES)", "area"],
  ],
  salas: [
    ["title", "Título (ES)"], ["titleEn", "Título (EN)"], ["eje", "Eje (ES)"], ["ejeEn", "Eje (EN)"],
    ["question", "Pregunta (ES)"], ["questionEn", "Pregunta (EN)"], ["kicker", "Kicker (ES)"], ["kickerEn", "Kicker (EN)"],
    ["image", "Imagen (ruta)"], ["imageAlt", "Alt imagen (ES)"], ["imageAltEn", "Alt imagen (EN)"],
    ["body", "Texto curatorial (ES)", "area"], ["bodyEn", "Texto curatorial (EN)", "area"],
    ["audioText", "Audioguía (ES)", "area"], ["audioTextEn", "Audioguía (EN)", "area"],
  ],
  agenda: [
    ["month", "Mes (septiembre/octubre)"], ["day", "Día", "number"], ["time", "Hora (ES)"], ["timeEn", "Hora (EN)"],
    ["title", "Título (ES)"], ["titleEn", "Título (EN)"], ["by", "Por (ES)"], ["byEn", "Por (EN)"],
    ["location", "Lugar (ES)"], ["locationEn", "Lugar (EN)"], ["kind", "Tipo (publica/privada/pendiente)"],
    ["description", "Descripción (ES)", "area"], ["descriptionEn", "Descripción (EN)", "area"],
  ],
};
const TR_FIELDS = [
  ["title", "Título (EN)"], ["tecnica", "Técnica (EN)"],
  ["practica", "Práctica (EN)", "area"], ["inspiracion", "Inspiración (EN)", "area"], ["bio", "Bio (EN)", "area"],
];

export default function AdminCatalogo() {
  useSEO({ title: "Catálogo | Admin Arteconciencia", noindex: true });
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  if (!token) return <AdminLogin onLogin={(t) => { localStorage.setItem(TOKEN_KEY, t); setToken(t); }} />;
  return <CatalogoDashboard token={token} onLogout={() => { localStorage.removeItem(TOKEN_KEY); setToken(null); }} />;
}

function CatalogoDashboard({ token, onLogout }) {
  const [kind, setKind] = useState("artists");
  const [doc, setDoc] = useState(null); // documento crudo del backend
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState("");
  const [editing, setEditing] = useState(null); // {item, translation, isNew, list}

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setDoc(await apiFetch(`/api/catalog/admin/${kind}`, token));
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [kind, token]);

  useEffect(() => { load(); }, [load]);

  // Lista plana de ítems del documento (agenda tiene events + performances)
  const items = useMemo(() => {
    if (!doc) return [];
    if (kind === "agenda") {
      return [
        ...(doc.events || []).map((e) => ({ ...e, _list: "events" })),
        ...(doc.performances || []).map((e) => ({ ...e, _list: "performances" })),
      ];
    }
    return doc.items || [];
  }, [doc, kind]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((it) =>
      [it.name, it.title, it.slug, it.id].filter(Boolean).join(" ").toLowerCase().includes(q)
    );
  }, [items, query]);

  const mainLabel = (it) => (kind === "artists" ? `${it.name || "—"} — ${it.title || "—"}` : kind === "salas" ? `${it.number || ""} · ${it.title || "—"}` : `${it.day || ""} ${it.month || ""} · ${it.title || "—"}`);
  const subLabel = (it) => (kind === "artists" ? `${it.price || "sin precio"} · /artistas/${it.slug}` : kind === "salas" ? `#sala-${it.id}` : `${it.time || ""} · ${it.kind || ""}${it._list === "performances" ? " · performance" : ""}`);

  const toggleHidden = async (it) => {
    const { _list, ...clean } = it;
    try {
      await apiFetch(`/api/catalog/admin/${kind}/items/${it[KEY_FIELD[kind]]}`, token, {
        method: "PUT",
        body: JSON.stringify({ item: { ...clean, hidden: !it.hidden } }),
      });
      toast.success(it.hidden ? "Visible nuevamente en el sitio público." : "Oculto del sitio público (conservado en el panel).");
      refreshCatalog();
      load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  return (
    <div className="paper-grain min-h-screen bg-paper pb-24">
      <AdminHeader onLogout={onLogout} />
      <div className="max-w-6xl mx-auto px-6 sm:px-10 pt-10">
        <p className="eyebrow text-latir-red">Arteconciencia · Admin</p>
        <h1 className="mt-2 font-display text-[2rem] sm:text-[2.6rem] text-ink" style={{ fontFamily: '"Instrument Serif", serif' }}>
          Catálogo
        </h1>
        <p className="mt-2 font-editorial italic text-[0.95rem] text-ink-soft">
          Los cambios se reflejan de inmediato en el sitio. Las fichas ocultas se conservan aquí; nada se borra.
        </p>

        <div className="mt-6 flex items-center gap-2 flex-wrap" data-testid="catalog-kind-tabs">
          {KINDS.map(([k, label]) => (
            <button
              key={k}
              onClick={() => { setKind(k); setQuery(""); setEditing(null); }}
              className={`px-3 min-h-[44px] border font-ui uppercase tracking-[0.14em] text-[0.62rem] transition-colors ${kind === k ? "border-ink bg-ink text-paper-raised" : "border-line text-ink-mute hover:text-ink hover:border-ink"}`}
              data-testid={`catalog-tab-${k}`}
            >
              {label}
            </button>
          ))}
          <span className="mx-1 h-6 w-px bg-line" aria-hidden />
          <button onClick={() => setEditing({ item: blankItem(kind, items), translation: {}, isNew: true })} className={BTN_SOLID} data-testid="catalog-new">
            <Plus className="w-3.5 h-3.5" /> {kind === "artists" ? "Nueva obra" : kind === "salas" ? "Nueva sala" : "Nuevo evento"}
          </button>
        </div>

        <div className="mt-5 relative max-w-md">
          <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-ink-mute" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Buscar por nombre, título o clave…"
            className={`${FIELD} !pl-9`}
            data-testid="catalog-search"
          />
        </div>

        {error && <div className="mt-6 border-l-2 border-latir-red px-4 py-3 font-ui text-[0.82rem] text-ink-soft" data-testid="catalog-error">{error}</div>}

        {loading ? (
          <div className="mt-6 flex items-center gap-3 text-ink-mute font-ui text-[0.9rem]"><Loader2 className="w-4 h-4 animate-spin" /> Cargando…</div>
        ) : (
          <ul className="mt-6 border-t border-b border-line divide-y divide-line" data-testid="catalog-list">
            {filtered.map((it) => (
              <li key={it[KEY_FIELD[kind]]} className="py-4 flex items-center gap-4 flex-wrap" data-testid={`catalog-item-${it[KEY_FIELD[kind]]}`}>
                <div className="flex-1 min-w-[220px]">
                  <p className={`font-editorial text-[1.02rem] ${it.hidden ? "text-ink-mute line-through" : "text-ink"}`}>{mainLabel(it)}</p>
                  <p className="mt-0.5 font-ui text-[0.72rem] text-ink-mute">{subLabel(it)}</p>
                </div>
                {it.hidden && (
                  <span className="font-ui uppercase tracking-[0.16em] text-[0.6rem] text-ink-mute border border-line px-2 py-1" data-testid={`catalog-hidden-${it[KEY_FIELD[kind]]}`}>Oculta</span>
                )}
                <button
                  onClick={() => setEditing({ item: it, translation: (doc?.translations || {})[it.slug] || {}, isNew: false })}
                  className={BTN_NEUTRAL}
                  data-testid={`catalog-edit-${it[KEY_FIELD[kind]]}`}
                >
                  Editar
                </button>
                <button onClick={() => toggleHidden(it)} className={BTN_NEUTRAL} data-testid={`catalog-toggle-${it[KEY_FIELD[kind]]}`}>
                  {it.hidden ? <><Eye className="w-3.5 h-3.5" /> Mostrar</> : <><EyeOff className="w-3.5 h-3.5" /> Ocultar</>}
                </button>
              </li>
            ))}
            {filtered.length === 0 && <li className="py-8 font-editorial italic text-ink-soft">Sin resultados.</li>}
          </ul>
        )}
      </div>

      {editing && (
        <ItemEditor
          kind={kind}
          editing={editing}
          token={token}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); refreshCatalog(); load(); }}
        />
      )}
    </div>
  );
}

function blankItem(kind, existing) {
  if (kind === "artists") {
    return { slug: "", name: "", title: "", discipline: "pintura", tecnica: "", medidas: "", year: "2026", price: "", instagram: "", practica: "", inspiracion: "", nacionalidad: "", bio: "", status: "disponible", image: "/brand/placeholder-obra.svg" };
  }
  if (kind === "salas") {
    const next = String(existing.length + 1).padStart(2, "0");
    return { id: "", number: next, title: "", titleEn: "", eje: "", ejeEn: "", question: "", questionEn: "", body: "", bodyEn: "", image: null, imageAlt: "", imageAltEn: "", artworkSlugs: [], audioText: "", audioTextEn: "" };
  }
  return { id: "", month: "octubre", day: 1, time: "", timeEn: "", title: "", titleEn: "", by: "", byEn: "", location: "Palacio Nacional", locationEn: "Palacio Nacional", description: "", descriptionEn: "", kind: "publica", _list: "events" };
}

/* ——— Editor de ítem: campos estructurados + JSON avanzado ——— */
function ItemEditor({ kind, editing, token, onClose, onSaved }) {
  const { isNew } = editing;
  const [draft, setDraft] = useState(() => ({ ...editing.item }));
  const [trDraft, setTrDraft] = useState(() => ({ ...editing.translation }));
  const [busy, setBusy] = useState(false);
  const [jsonOpen, setJsonOpen] = useState(false);
  const [jsonText, setJsonText] = useState("");
  const kf = KEY_FIELD[kind];

  const set = (key, val) => setDraft((d) => ({ ...d, [key]: val }));
  const setTr = (key, val) => setTrDraft((d) => ({ ...d, [key]: val }));

  const save = async () => {
    setBusy(true);
    try {
      const clean = { ...draft };
      delete clean._list;
      if (kind === "salas" && typeof clean.artworkSlugs === "string") {
        clean.artworkSlugs = clean.artworkSlugs.split(",").map((s) => s.trim()).filter(Boolean);
      }
      if (kind === "agenda") {
        clean.day = parseInt(clean.day, 10) || 1;
        if (isNew) clean._list = editing.item._list || "events";
      }
      if (isNew && kind === "artists" && !clean.slug) clean.slug = slugifyTitle(clean.name);
      if (isNew && kind === "salas" && !clean.id) clean.id = slugifyTitle(clean.title);
      const payload = kind === "artists" ? { item: clean, translation: trDraft } : { item: clean };
      await apiFetch(
        isNew ? `/api/catalog/admin/${kind}/items` : `/api/catalog/admin/${kind}/items/${editing.item[kf]}`,
        token,
        { method: isNew ? "POST" : "PUT", body: JSON.stringify(payload) }
      );
      toast.success(isNew ? "Creado correctamente." : "Cambios guardados y visibles en el sitio.");
      onSaved();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  const applyJson = () => {
    try {
      const parsed = JSON.parse(jsonText);
      if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) throw new Error("Debe ser un objeto JSON");
      setDraft(parsed);
      setJsonOpen(false);
      toast.success("JSON aplicado al formulario.");
    } catch (e) {
      toast.error(`JSON inválido: ${e.message}`);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-ink/40 flex items-end sm:items-center justify-center" onClick={onClose}>
      <div
        className="w-full sm:max-w-2xl bg-paper border border-line px-6 sm:px-8 pt-6 overflow-y-auto max-h-[90dvh]"
        style={{ paddingBottom: "calc(1.5rem + env(safe-area-inset-bottom))" }}
        onClick={(e) => e.stopPropagation()}
        data-testid="catalog-editor"
      >
        <div className="flex items-baseline justify-between gap-4">
          <h3 className="font-display text-[1.4rem] text-ink" style={{ fontFamily: '"Instrument Serif", serif' }}>
            {isNew ? "Nuevo ítem" : `Editar: ${draft.name || draft.title || draft[kf]}`}
          </h3>
          <button onClick={onClose} className="p-2 text-ink-mute hover:text-ink" aria-label="Cerrar" data-testid="catalog-editor-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Clave / URL: inmutable al editar (protege URLs y QR impresos) */}
        <div className="mt-3 border border-line bg-paper-raised px-4 py-3">
          <p className="font-ui uppercase tracking-[0.18em] text-[0.6rem] text-ink-mute">Clave pública (URL) — no editable</p>
          {isNew ? (
            <input
              value={draft[kf] || ""}
              onChange={(e) => set(kf, slugifyTitle(e.target.value))}
              placeholder={kind === "artists" ? "se genera del nombre" : kind === "salas" ? "se genera del título" : "automática"}
              className={`${FIELD} mt-2`}
              data-testid="catalog-editor-key"
            />
          ) : (
            <p className="mt-1 font-ui text-[0.85rem] text-ink" data-testid="catalog-editor-key">{draft[kf]}</p>
          )}
          {kind === "artists" && draft.qrSlug && !isNew && (
            <p className="mt-1 font-ui text-[0.68rem] text-ink-mute">QR impreso: /obra/{draft.qrSlug}</p>
          )}
        </div>

        <div className="mt-5 grid sm:grid-cols-2 gap-4">
          {FIELDS[kind].map(([key, label, type]) => (
            <label key={key} className={`block ${type === "area" ? "sm:col-span-2" : ""}`}>
              <span className={LABEL}>{label}</span>
              {type === "area" ? (
                <textarea rows={4} value={draft[key] ?? ""} onChange={(e) => set(key, e.target.value)} className={FIELD} data-testid={`catalog-field-${key}`} />
              ) : (
                <input type="text" value={draft[key] ?? ""} onChange={(e) => set(key, type === "number" ? e.target.value.replace(/\D/g, "") : e.target.value)} className={FIELD} data-testid={`catalog-field-${key}`} />
              )}
            </label>
          ))}
          {kind === "salas" && (
            <label className="block sm:col-span-2">
              <span className={LABEL}>Obras de la sala (slugs separados por coma)</span>
              <input
                type="text"
                value={Array.isArray(draft.artworkSlugs) ? draft.artworkSlugs.join(", ") : draft.artworkSlugs || ""}
                onChange={(e) => set("artworkSlugs", e.target.value)}
                className={FIELD}
                data-testid="catalog-field-artworkSlugs"
              />
            </label>
          )}
        </div>

        {kind === "artists" && (
          <div className="mt-6 border-t border-line pt-5">
            <p className="font-ui uppercase tracking-[0.2em] text-[0.68rem] text-ink mb-4">Traducción al inglés</p>
            <div className="grid sm:grid-cols-2 gap-4">
              {TR_FIELDS.map(([key, label, type]) => (
                <label key={key} className={`block ${type === "area" ? "sm:col-span-2" : ""}`}>
                  <span className={LABEL}>{label}</span>
                  {type === "area" ? (
                    <textarea rows={3} value={trDraft[key] ?? ""} onChange={(e) => setTr(key, e.target.value)} className={FIELD} data-testid={`catalog-tr-${key}`} />
                  ) : (
                    <input type="text" value={trDraft[key] ?? ""} onChange={(e) => setTr(key, e.target.value)} className={FIELD} data-testid={`catalog-tr-${key}`} />
                  )}
                </label>
              ))}
            </div>
          </div>
        )}

        <div className="mt-6 border-t border-line pt-4">
          <button
            onClick={() => { setJsonOpen((v) => !v); if (!jsonOpen) setJsonText(JSON.stringify(draft, null, 2)); }}
            className="font-ui uppercase tracking-[0.18em] text-[0.62rem] text-ink-mute hover:text-ink transition-colors"
            data-testid="catalog-json-toggle"
          >
            {jsonOpen ? "− Ocultar JSON avanzado" : "+ JSON avanzado (todos los campos)"}
          </button>
          {jsonOpen && (
            <div className="mt-3">
              <textarea rows={10} value={jsonText} onChange={(e) => setJsonText(e.target.value)} className={`${FIELD} font-mono !text-[0.72rem]`} data-testid="catalog-json" />
              <button onClick={applyJson} className={`${BTN_NEUTRAL} mt-2`} data-testid="catalog-json-apply">Aplicar JSON al formulario</button>
            </div>
          )}
        </div>

        <div className="mt-6 flex items-center gap-2 flex-wrap">
          <button onClick={save} disabled={busy} className={BTN_SOLID} data-testid="catalog-save">
            {busy ? "Guardando…" : isNew ? "Crear" : "Guardar cambios"}
          </button>
          <button onClick={onClose} disabled={busy} className={BTN_NEUTRAL} data-testid="catalog-cancel">Cancelar</button>
        </div>
      </div>
    </div>
  );
}
